import IndexLayout from '../layouts';
import { Link, withPrefix } from 'gatsby';

import LazyLoad from 'react-lazyload';

import Wrapper from '../components/Wrapper';
import SiteNav from '../components/header/SiteNav';
import { SiteHeader, outer, inner, SiteMain } from '../styles/shared';
import * as React from 'react';
import { css } from '@emotion/core';
import { PostFullHeader, PostFullTitle, NoImage, PostFull } from '../templates/post';
import { PostFullContent } from '../components/PostContent';
import Footer from '../components/Footer';
import Helmet from 'react-helmet';
import { colors } from '../styles/colors';
import BackToTop from '../components/BackToTop';
import config from '../website-config';

const PageTemplate = css`
  .site-main {
    background #fff;
    padding-bottom: 4vw;
  }

  .post-content {
    max-width: 1200px;
    margin: 0 auto;
  }

  .tools-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 2rem;
  }
  .tools-grid a {
    text-decoration: ${colors.beta} 1px underline;
    transition: text-decoration 0.25s;
  }

  .tools-grid a:hover {
    text-decoration: ${colors.alpha} 1px underline;
  }

  .tools-grid img {
    box-shadow: rgba(39,44,49,0.06) 8px 14px 38px, rgba(39,44,49,0.03) 1px 3px 8px;
    border-radius: 5px;
    transition: transform .2s ease-out;
  }
  .tools-grid img:hover {
    transform: scale(1.03);
  }

  a {
    box-shadow: none !important;
  }
`;

const Tools: React.FunctionComponent = () => (
  <IndexLayout>
    <Helmet>
      <title>Free Online Photography and Retouching Tools/Resources | Giggster Guide</title>
      <meta
        name="description"
        content="Free online photography and retouching tools, reference pages and resources. You'll find some interactive tools to help you with your photography skills."
      />

      <meta property="og:site_name" content="Giggster Guide" />
      <meta property="og:type" content="article" />
      <meta property="og:title" content="Free Online Photography and Retouching Tools/Resources" />
      <meta
        property="og:description"
        content="Free online photography and retouching tools, reference pages and resources. You'll find some interactive tools to help you with your photography skills."
      />
      <meta property="og:url" content="https://giggster.com/guide/tools/" />
      <meta property="og:image" content="https://giggster.com/guide/covers/tools-cover.jpg" />
      <meta property="article:publisher" content="https://www.facebook.com/giggsterHQ" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Free Online Photography and Retouching Tools/Resources" />
      <meta
        name="twitter:description"
        content="Free online photography and retouching tools, reference pages and resources. You'll find some interactive tools to help you with your photography skills."
      />
      <meta name="twitter:url" content="https://giggster.com/guide/tools/" />
      <meta name="twitter:image" content="https://giggster.com/guide/covers/tools-cover.jpg" />
      <meta name="twitter:site" content="@Giggster" />
      <meta name="twitter:creator" content="@Giggster" />
    </Helmet>
    <Wrapper css={PageTemplate}>
      <header css={[outer, SiteHeader]}>
        <div css={inner}>
          <SiteNav />
        </div>
      </header>
      <main id="site-main" className="site-main" css={[SiteMain, outer]}>
        <article className="post page" css={[PostFull, NoImage]}>
          <PostFullHeader>
            <PostFullTitle>Free Photography &amp; Retouching Tools</PostFullTitle>
          </PostFullHeader>

          <PostFullContent className="post-full-content">
            <div className="post-content text-center">
              <p>
                Here you'll find some free tools, resources and reference pages that I've put
                together to help you out with your photography, retouching or workflow:
              </p>
              <h2>Free Resources</h2>

              <div className="tools-grid">
                <Link to="/free-luts/">
                  <figure>
                    <img alt="" src={withPrefix("/covers/free-luts-3.jpg")} />
                    <figcaption>Free LUTs for Photoshop</figcaption>
                  </figure>
                </Link>
                <Link to="/capture-one-styles/">
                  <figure>
                    <img alt="" src={withPrefix("/covers/capture-one-styles-s.jpg")} />
                    <figcaption>25 Free Capture One Styles</figcaption>
                  </figure>
                </Link>
                <Link to="/texture-overlays/">
                  <figure>
                    <img alt="" src={withPrefix("/covers/texture-overlays.jpg")} />
                    <figcaption>Free Texture Overlays for Photoshop</figcaption>
                  </figure>
                </Link>
                <Link to="/light-leaks/">
                  <figure>
                    <img alt="" src={withPrefix("/covers/light-leaks.jpg")} />
                    <figcaption>Free Light Leaks</figcaption>
                  </figure>
                </Link>
                <Link to="/bokeh-overlays/">
                  <LazyLoad height={200}>
                    <figure>
                      <img alt="" src={withPrefix("/covers/bokeh-overlays.jpg")} />
                      <figcaption>220 Bokeh Overlays for Photoshop</figcaption>
                    </figure>
                  </LazyLoad>
                </Link>

                <Link to="/light-trails/">
                  <LazyLoad height={200}>
                    <figure>
                      <img alt="" src={withPrefix("/covers/light-trails-cover-l.jpg")} />
                      <figcaption>Light Trail Overlays</figcaption>
                    </figure>
                  </LazyLoad>
                </Link>
                <Link to="/creepy-spooky-overlays/">
                  <LazyLoad height={200}>
                    <figure>
                      <img alt="" src={withPrefix("/covers/creepy-spooky-overlays-s.jpg")} />
                      <figcaption>37 Creepy/Spooky Overlays</figcaption>
                    </figure>
                  </LazyLoad>
                </Link>
                <Link to="/brick-textures/">
                  <LazyLoad height={200}>
                    <figure>
                      <img alt="" src={withPrefix("/covers/brick-textures-s.jpg")} />
                      <figcaption>55 Free Brick Textures</figcaption>
                    </figure>
                  </LazyLoad>
                </Link>
              </div>
              <hr />
              <h2>Interactive Tools</h2>
              <div className="tools-grid">
                <Link to="/color-grading/" title="Interactive color grading helper">
                  <LazyLoad height={200}>
                    <figure>
                      <img alt="" src={withPrefix("/covers/color-grading-s.jpg")} />
                      <figcaption>Color Grading Helper</figcaption>
                    </figure>
                  </LazyLoad>
                </Link>
                <Link to="/black-and-white-converter/" title="black and white converter">
                  <LazyLoad height={200}>
                    <figure>
                      <img alt="" src={withPrefix("/covers/black-and-white-converter-s.jpg")} />
                      <figcaption>Convert images to black and white</figcaption>
                    </figure>
                  </LazyLoad>
                </Link>
                <Link to="/duotone-generator/" title="Duotone Generator">
                  <LazyLoad height={200}>
                    <figure>
                      <img alt="" src={withPrefix("/covers/duotone-generator-s.jpg")} />
                      <figcaption>Duotone Generator with Photoshop Actions</figcaption>
                    </figure>
                  </LazyLoad>
                </Link>
                <Link to="/complementary-colors/" title="Complementary Color Picker">
                  <LazyLoad height={200}>
                    <figure>
                      <img alt="" src={withPrefix("/covers/complementary-colors2.svg")} />
                      <figcaption>Complementary Color Picker</figcaption>
                    </figure>
                  </LazyLoad>
                </Link>
                <Link to="/color-extractor/" title="Photo color extractor">
                  <LazyLoad height={200}>
                    <figure>
                      <img alt="" src={withPrefix("/covers/color-extractor-s.jpg")} />
                      <figcaption>Color Extractor / Color Palette</figcaption>
                    </figure>
                  </LazyLoad>
                </Link>
                <Link to="/rain-texture-generator/" title="Rain texture generator">
                  <LazyLoad height={200}>
                    <figure>
                      <img alt="" src={withPrefix("/covers/rain-texture-generator-s.jpg")} />
                      <figcaption>Rain Texture Generator</figcaption>
                    </figure>
                  </LazyLoad>
                </Link>
                <Link to="/cloud-texture-generator/" title="Cloud texture generator">
                  <LazyLoad height={200}>
                    <figure>
                      <img alt="" src={withPrefix("/covers/cloud-texture-generator-s.jpg")} />
                      <figcaption>Cloud Texture Generator</figcaption>
                    </figure>
                  </LazyLoad>
                </Link>
                <Link to="/crop-factor-calculator/" title="Crop Factor Calculator">
                  <LazyLoad height={200}>
                    <figure>
                      <img alt="" src={withPrefix("/covers/crop-factor-calculator-cover-1200w.jpg")} />
                      <figcaption>Crop Factor Calculator</figcaption>
                    </figure>
                  </LazyLoad>
                </Link>
                <Link to="/aperture-visualizer/" title="Aperture Visualizer">
                  <LazyLoad height={200}>
                    <figure>
                      <img alt="" src={withPrefix("/covers/aperture-visualizer.svg")} />
                      <figcaption>Aperture Visualizer</figcaption>
                    </figure>
                  </LazyLoad>
                </Link>
                <Link to="/color-temperature-chart/" title="Interactive Color Temperature Chart">
                  <LazyLoad height={200}>
                    <figure>
                      <img alt="" src={withPrefix("/covers/color-temperature-chart.svg")} />
                      <figcaption>Color Temperature Chart</figcaption>
                    </figure>
                  </LazyLoad>
                </Link>
              </div>

              <hr />

              <h2>Reference Pages</h2>

              <div className="tools-grid">
                <LazyLoad height={200}>
                  <Link to="/capture-one-keyboard-shortcuts/">
                    <figure>
                      <img alt="" src={withPrefix("/covers/capture-one-kb-shortcuts.svg")} />
                      <figcaption>Capture One Pro Keyboard Shortcuts</figcaption>
                    </figure>
                  </Link>
                </LazyLoad>
                <LazyLoad height={200}>
                  <Link to="/keyboard-shortcuts/">
                    <figure>
                      <img alt="" src={withPrefix("/covers/ps-kb-shortcuts.svg")} />
                      <figcaption>Photoshop CC Keyboard Shortcuts</figcaption>
                    </figure>
                  </Link>
                </LazyLoad>
                <LazyLoad height={200}>
                  <Link to="/keyboard-shortcuts-2/">
                    <figure>
                      <img alt="" src={withPrefix("/covers/lightroom-classic-kb-shortcuts.svg")} />
                      <figcaption>Lightroom Classic CC Keyboard Shortcuts</figcaption>
                    </figure>
                  </Link>
                </LazyLoad>
              </div>
            </div>
          </PostFullContent>
        </article>
      </main>
      <BackToTop />
      <Footer />
    </Wrapper>
  </IndexLayout>
);

export default Tools;
